import React from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const OrRecyclePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <img 
          src="/assets/images/bague-or-18-carats-diamants-aupiho-joaillerie.jpeg"
          alt="Bague en or 18 carats avec diamants - Aupiho Joaillerie"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ position: 'fixed' }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-wide" style={{ fontFamily: 'MoonTime, cursive' }}>
              Or recycle
            </h1>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-px bg-[#c69c70]"></div>
              <SparklesIcon className="h-8 w-8 text-[#c69c70]" />
              <div className="w-12 h-px bg-[#c69c70]"></div>
            </div>
            <p className="text-xl text-white/90 font-light max-w-2xl mx-auto">
              Une joaillerie éthique et responsable pour préserver notre planète
            </p>
          </div>
        </div>
      </section>

      {/* L'or recyclé : une valeur infinie */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl font-light text-[#c69c70] mb-6 tracking-wide">L'or recyclé : une valeur infinie</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-base">
                <p>
                  Chez Aupiho Joaillerie, nous croyons en la beauté et l'<strong className="text-[#497aab]">éternité de l'or</strong>. Grâce à ses propriétés uniques, l'or est éternel et peut être recyclé à l'infini sans perdre de sa splendeur ni de sa qualité. Depuis sa découverte, il y a plus de 6 000 ans, seulement 2 % du volume extrait a disparu. <strong className="text-[#497aab]">L'or est une matière parfaitement recyclable</strong>, et est certainement la première matière à avoir été recyclée. Chez Aupiho Joaillerie nous avons fait le choix d'utiliser uniquement dans nos créations de <strong className="text-[#497aab]">l'or 18 carats</strong> (750/1000ème) recyclé.
                </p>
              </div>
            </div>
            
            <div className="order-1 md:order-2">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="/assets/images/or-recycle/or-brut.jpg" 
                  alt="Or recyclé - pépites d'or" 
                  className="w-full h-full object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#c69c70]/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement pour une joaillerie éthique et responsable */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div className="order-1 md:order-1">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="/assets/images/or-recycle/forge-or.jpg" 
                  alt="Forge d'or - artisan joaillier" 
                  className="w-full h-full object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#c69c70]/20 to-transparent"></div>
              </div>
            </div>

            <div className="order-2 md:order-2">
              <h2 className="text-4xl font-light text-[#c69c70] mb-6 tracking-wide">Engagement pour une joaillerie éthique et responsable</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-base">
                <p>
                  Notre <strong className="text-[#497aab]">or recyclé</strong> provient exclusivement d'affineurs <strong className="text-[#497aab]">certifiés CoC</strong> (Chain Of Custody), garantissant une chaîne de traçabilité responsable et une provenance clairement définie. Grâce à cette certification, nous pouvons assurer à nos clients que l'or que nous utilisons provient de <strong className="text-[#497aab]">sources responsables</strong> et qu'il a été manipulé selon des normes strictes en matière de durabilité et d'éthique. De plus, notre or est fondu en France, permettant de maintenir un approvisionnement en circuit court. Tous nos artisans et affineurs sont <strong className="text-[#497aab]">certifiés RJC</strong> (Responsible Jewellery Council) témoignant de notre engagement envers des pratiques éthiques et responsables. <strong className="text-[#497aab]">L'or recyclé</strong> provient de diverses sources : industrie, informatique, rachat auprès de particuliers... Il est directement recyclé auprès d'affineurs en France, avec les standards légaux français.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi choisir de l'or recyclé ? */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-light text-[#c69c70] mb-8 tracking-wide text-center">Pourquoi choisir de l'or recyclé ?</h2>
            <div className="space-y-6 text-gray-700 leading-relaxed text-base">
              <p>
                Le choix de <strong className="text-[#497aab]">l'or recyclé</strong> pour une <strong className="text-[#497aab]">joaillerie durable et éthique</strong> chez Aupiho Joaillerie n'est pas simplement une option, mais nous paraît comme une « solution » lorsque l'on prend conscience de l'impact environnemental de l'extraction de l'or. Selon le World Gold Council et le US Geological Survey, environ 75% des réserves d'or de la planète sont déjà en circulation. Pourtant, l'extraction aurifère continue à avoir des conséquences désastreuses sur l'environnement.
              </p>
              
              <p>
                Pour extraire seulement <strong className="text-[#497aab]">1 gramme d'or</strong>, il faut en moyenne :
              </p>
              
              <ul className="space-y-3 ml-6">
                <li className="flex items-start gap-3">
                  <span className="text-[#c69c70] mt-1">•</span>
                  <span><strong>2 tonnes de déchets miniers</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c69c70] mt-1">•</span>
                  <span><strong>2000 litres d'eau</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c69c70] mt-1">•</span>
                  <span><strong>25 kg de CO2</strong></span>
                </li>
              </ul>

              <p className="pt-4">
                Sans compter l'utilisation de produits chimiques dangereux tels que le mercure et le cyanure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Notre contribution à un futur durable */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl font-light text-[#c69c70] mb-6 tracking-wide">Notre contribution à un futur durable</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-base">
                <p>
                  En choisissant de <strong className="text-[#497aab]">l'or recyclé</strong>, nous participons à réduire les effets négatifs de l'extraction minière et soutenons des pratiques plus durables. Notre engagement est de garantir que chaque bijou porte des <strong className="text-[#497aab]">valeurs éthiques</strong> et qui <strong className="text-[#497aab]">respecte la nature</strong> et nos valeurs.
                </p>
                <p>
                  L'utilisation de <strong className="text-[#497aab]">l'or recyclé</strong> permet de diminuer notre empreinte carbone et de contribuer à la préservation des ressources naturelles. Chaque gramme d'<strong className="text-[#497aab]">or recyclé</strong> utilisé dans nos ateliers est un pas de plus vers une industrie de la joaillerie plus propre et plus <strong className="text-[#497aab]">respectueuse de l'environnement</strong>.
                </p>
              </div>
            </div>
            
            <div className="order-1 md:order-2">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="/assets/images/or-recycle/bijoux-or-recycle.jpg" 
                  alt="Bijoux en or recyclé" 
                  className="w-full h-full object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#c69c70]/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* L'engagement d'Aupiho : l'or recyclé */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div className="order-1 md:order-1">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="/assets/images/or-recycle/bagues-main.jpg" 
                  alt="Bagues en or recyclé - engagement Aupiho" 
                  className="w-full h-full object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#c69c70]/20 to-transparent"></div>
              </div>
            </div>

            <div className="order-2 md:order-2">
              <h2 className="text-4xl font-light text-[#c69c70] mb-6 tracking-wide">L'engagement d'Aupiho : l'or recyclé</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-base">
                <p>
                  En optant pour <strong className="text-[#497aab]">l'or recyclé</strong>, vous choisissez non seulement la beauté mais aussi la <strong className="text-[#497aab]">responsabilité</strong>. Nos créations incarnent non seulement le luxe et l'élégance, mais aussi un engagement envers la <strong className="text-[#497aab]">préservation de l'environnement</strong> et des <strong className="text-[#497aab]">pratiques éthiques</strong>. Nous croyons que la beauté véritable ne se mesure pas seulement par l'éclat extérieur d'un bijou, mais aussi par les valeurs qu'il représente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rejoignez-nous dans ce mouvement */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-light text-[#c69c70] mb-8 tracking-wide text-center">Rejoignez-nous dans ce mouvement</h2>
            <div className="space-y-6 text-gray-700 leading-relaxed text-base text-center">
              <p>
                Cette fabrication de bijoux avec de <strong className="text-[#497aab]">l'or durable</strong> ne fait aucune concession sur la qualité, nos bijoux sont faits pour durer dans le temps, être transmis...
              </p>
              <p>
                En choisissant Aupiho Joaillerie, vous faites le choix de <strong className="text-[#497aab]">bijoux précieux</strong> qui racontent aussi une histoire d'engagement et de respect.
              </p>
              <p>
                Découvrez <Link to="/categories" className="text-[#c69c70] hover:text-[#b88a5f] font-medium underline">nos collections</Link> et faites partie de ce mouvement vers une <strong className="text-[#497aab]">joaillerie plus éthique et durable</strong> avec Aupiho Joaillerie. Nos bijoux, conçus avec passion et conscience, sont l'incarnation de l'élégance durable. Ensemble, faisons le choix d'une joaillerie éthique et célébrons la beauté de <strong className="text-[#497aab]">l'or recyclé</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Le rachat d'or */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-light text-[#c69c70] mb-8 tracking-wide text-center">Le rachat d'or</h2>
            <div className="space-y-6 text-gray-700 leading-relaxed text-base">
              <p>
                Chez Aupiho Joaillerie, nous vous offrons la possibilité de donner une nouvelle vie à vos bijoux oubliés. Que ce soient des bijoux de famille, des chaînes abîmées, des médailles ou des bijoux cassés que vous ne portez plus, ne les laissez pas dormir dans un tiroir ! En nous les confiant, vous bénéficiez d'une expertise en rachat d'or et d'un service personnalisé, respectueux de l'histoire de chaque pièce. Transformez ces trésors en nouvelles créations ou en souvenirs précieux, adaptés à votre style et à vos envies.
              </p>
              <p>
                Nous vous proposons de <strong className="text-[#497aab]">racheter</strong> ou <Link to="/contactez-nous" className="text-[#c69c70] hover:text-[#b88a5f] font-medium underline">cours de rachat de l'or</Link>. Le cours de l'or varie quotidiennement en fonction et sa valeur a fortement augmenté depuis ces 5 dernières années. Pour plus d'information ou pour toute demande n'hésitez pas à <Link to="/contactez-nous" className="text-[#c69c70] hover:text-[#b88a5f] font-medium underline">nous contacter</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OrRecyclePage;

